import { Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { AppShell } from "@mantine/core";
import { IconList, IconFolder, IconAlertTriangle, IconBug, IconActivity, IconScale, IconSettings } from "@tabler/icons-react";
import { ModeBanner } from "./components/ModeBanner.js";
import { RulesList } from "./pages/RulesList.js";
import { RuleEdit } from "./pages/RuleEdit.js";
import { ClientFoldersList } from "./pages/ClientFoldersList.js";
import { ClientFolderEdit } from "./pages/ClientFolderEdit.js";
import { UnhandledSenders } from "./pages/UnhandledSenders.js";
import { DeadLetter } from "./pages/DeadLetter.js";
import { Decisions } from "./pages/Decisions.js";
import { Settings } from "./pages/Settings.js";
import { ShadowCompare } from "./pages/ShadowCompare.js";
import { AuthGate } from "./auth/AuthGate.js";
import { useUser } from "./auth/useUser.js";
import logo from "./assets/boxtechs-logo.png";
import styles from "./App.module.css";
import { useQuery } from "@tanstack/react-query";
import { configApi } from "./api/config.js";

const NAV = [
  { group: "Sorting", items: [
    { to: "/rules",     label: "Rules",             icon: IconList },
    { to: "/folders",   label: "Client folders",    icon: IconFolder },
    { to: "/unhandled", label: "Unhandled senders", icon: IconAlertTriangle },
  ]},
  { group: "Operations", items: [
    { to: "/dead",      label: "Dead-letter",     icon: IconBug },
    { to: "/decisions", label: "Decisions",       icon: IconActivity },
    { to: "/shadow",    label: "Shadow compare",  icon: IconScale },
    { to: "/settings",  label: "Settings",        icon: IconSettings },
  ]},
];

export default function App() {
  const loc = useLocation();
  const user = useUser();
  const name = user?.userDetails?.split("@")[0] ?? "Admin";
  const initials = (name.replace(/[^a-zA-Z]/g, "").slice(0, 2) || "BT").toUpperCase();
  const { data: config } = useQuery({ queryKey: ["config"], queryFn: configApi.get });
  const logoSrc = config?.logoDataUri || logo;

  return (
    <AuthGate>
      <AppShell padding={0} navbar={{ width: 248, breakpoint: "sm" }}>
        <AppShell.Navbar p={0} withBorder={false}>
          <div className={styles.navbar}>
            <div className={styles.brand}>
              <img src={logoSrc} alt="BoxTechs" />
            </div>
            {NAV.map((g) => (
              <div key={g.group}>
                <div className={styles.label}>{g.group}</div>
                <nav className={styles.section}>
                  {g.items.map((it) => {
                    const Icon = it.icon;
                    const active = loc.pathname.startsWith(it.to);
                    return (
                      <Link
                        key={it.to}
                        to={it.to}
                        className={`${styles.item} ${active ? styles.itemActive : ""}`}
                        aria-current={active ? "page" : undefined}
                      >
                        <Icon size={18} />
                        {it.label}
                      </Link>
                    );
                  })}
                </nav>
              </div>
            ))}
            <div className={styles.spacer} />
            <div className={styles.footer}>
              <div className={styles.avatar}>{initials}</div>
              <div className={styles.who}>{name}<span>Administrator</span></div>
            </div>
          </div>
        </AppShell.Navbar>
        <AppShell.Main>
          <ModeBanner />
          <div style={{ maxWidth: 1500, margin: "0 auto", padding: "24px 24px 48px" }}>
            <Routes>
              <Route path="/" element={<Navigate to="/rules" replace />} />
              <Route path="/rules"     element={<RulesList />} />
              <Route path="/rules/new" element={<RuleEdit />} />
              <Route path="/rules/:id" element={<RuleEdit />} />
              <Route path="/folders"          element={<ClientFoldersList />} />
              <Route path="/folders/new"      element={<ClientFolderEdit />} />
              <Route path="/folders/:domain"  element={<ClientFolderEdit />} />
              <Route path="/unhandled" element={<UnhandledSenders />} />
              <Route path="/dead"      element={<DeadLetter />} />
              <Route path="/decisions" element={<Decisions />} />
              <Route path="/shadow"    element={<ShadowCompare />} />
              <Route path="/settings"  element={<Settings />} />
              <Route path="*"          element={<Navigate to="/rules" replace />} />
            </Routes>
          </div>
        </AppShell.Main>
      </AppShell>
    </AuthGate>
  );
}
